import React, { useEffect, useState } from "react";
import close from "../../assets/icons/close.svg";
import next from "../../assets/icons/icon.svg";
import { getData, Postdata, UpdateDataFormWithJson } from "../../API/GlobalApi";
import Select from "react-select";
import { reactSelectStyles } from "../../Utils/selectBoxStyle";
import Notifier from "../../Utils/notify";
import SpinnerLoader from "../../Utils/Spinner";

export const AddBankAccounts = ({
  isOpen,
  onClose,
  editData,
  getInstitutionsBank,
  currentPage,
  mode,
}) => {
  const [bankNames, setBankNames] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Account type options
  const accountTypeOptions = [
    { value: "Savings", label: "Savings" },
    { value: "Current", label: "Current" },
    { value: "OD", label: "OD" },
    { value: "CC", label: "CC" },
    { value: "Other", label: "Other" },
  ];

  const [formData, setFormData] = useState({
    bankId: null,
    accountType: null,
    accountHolderName: "",
    accountNumber: "",
    ifscCode: "",
    micrCode: "",
    branchAddress: "",
    isDefault: false,
    isPGDefault: false,
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && editData) {
        setFormData({
          bankId: editData?.bankId || null,
          accountType: editData?.account_type || null,
          accountHolderName: editData?.account_holder_name || "",
          accountNumber: editData?.account_number || "",
          ifscCode: editData?.ifsc_code || "",
          micrCode: editData?.micr_code || "",
          branchAddress: editData?.branch_name || "",
          isDefault: editData?.is_default || false,
          isPGDefault: editData?.default_online_account || false,
        });
      } else {
        // reset for create
        setFormData({
          bankId: null,
          accountType: null,
          accountHolderName: "",
          accountNumber: "",
          ifscCode: "",
          micrCode: "",
          branchAddress: "",
          isDefault: false,
          isPGDefault: false,
        });
      }
      setErrors({});
    }
  }, [isOpen, mode, editData]);

  // fetch bank list
  const getBankDetails = async () => {
    const res = await getData("staff/teacher-official-details/get-master-data/");
    setBankNames(res?.master?.banks || []);
  };

  const bankOptions = bankNames.map((bank) => ({
    value: bank.id,
    label: bank.name,
  }));

  useEffect(() => {
    getBankDetails();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleBankChange = (selected) => {
    const value = selected?.value || null;
    setFormData((prev) => ({ ...prev, bankId: value }));
    validateField("bankId", value);
  };

  const handleAccountTypeChange = (selected) => {
    const value = selected?.value || null;
    setFormData((prev) => ({ ...prev, accountType: value }));
    validateField("accountType", value);
  };

  const handleDefaultChange = (e) => {
    setFormData((prev) => ({ ...prev, isDefault: e.target.checked }));
  };


  const handlePGDefaultChange = (e) => {
    setFormData((prev) => ({ ...prev, isPGDefault: e.target.checked }));
  };

  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "bankId":
        if (!value) error = "Bank is required";
        break;
      case "accountType":
        if (!value) error = "Account Type is required";
        break;
      case "accountHolderName":
        if (!value.trim()) error = "Account Holder Name is required";
        else if (value.length < 3) error = "Name must be at least 3 characters";
        else if (!/^[a-zA-Z\s.]+$/.test(value))
          error = "Name can only contain letters and spaces";
        break;
      case "accountNumber":
        if (!value.trim()) error = "Account Number is required";
        else if (!/^[0-9]{9,18}$/.test(value))
          error = "Account Number must be 9–18 digits";
        break;
      case "ifscCode":
        if (!value.trim()) error = "IFSC Code is required";
        else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value))
          error = "Invalid IFSC Code (e.g., SBIN0001234)";
        break;
      case "micrCode":
        if (value && !/^[0-9]{9}$/.test(value))
          error = "MICR Code must be exactly 9 digits";
        break;

        break;
      case "branchAddress":
        if (!value.trim()) error = "Branch Address is required";
        else if (value.length < 5)
          error = "Branch Address must be at least 5 characters";
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateAllFields = () => {
    let valid = true;
    Object.entries(formData).forEach(([key, value]) => {
      validateField(key, value);

      // Skip validation for checkboxes (isDefault, isPGDefault)
      if (
        (value === "" || value === null) &&
        key !== "isDefault" &&
        key !== "isPGDefault" &&
          key !== "micrCode"
      ) {
        valid = false;
      }
    });
    return valid;
  };

  const handleSubmit = async () => {
    const isValid = validateAllFields();
    if (!isValid) return;

    setLoading(true);
    try {
      const payload = {
        bank: formData.bankId,
        account_type: formData.accountType,
        account_holder_name: formData.accountHolderName,
        account_number: formData.accountNumber,
        ifsc_code: formData.ifscCode,
        micr_code: formData.micrCode,
        branch_name: formData.branchAddress,
        is_active: true,
        is_default: formData.isDefault,
        default_online_account: formData.isPGDefault,
      };

      let response;

      if (mode === "edit" && editData?.id) {
        response = await UpdateDataFormWithJson(
          `finance/institution-bank-accounts/${editData.id}/`,
          payload
        );

        getInstitutionsBank(currentPage);
      } else {
        response = await Postdata("finance/institution-bank-accounts/", payload);

        getInstitutionsBank();
      }

      // 🔎 Handle errors
      if (response?.error) {
        // case: { error: "msg" } OR { error: ["msg1","msg2"] }
        const msgs = Array.isArray(response.error)
          ? response.error
          : [response.error];
        msgs.forEach((msg) => Notifier.error(msg));
        return; // stop here
      }

      if (
        Object.keys(response).some(
          (key) => Array.isArray(response[key]) && response[key].length
        )
      ) {
        // case: { account_type: ["invalid choice"] }
        Object.entries(response).forEach(([field, messages]) => {
          (Array.isArray(messages) ? messages : [messages]).forEach((msg) =>
            Notifier.error(`${field}: ${msg}`)
          );
        });
        return; // stop here
      }

      // ✅ success
      Notifier.success(
        mode === "edit"
          ? "Account Details updated successfully"
          : "Account created successfully"
      );
      onClose();
    } catch (error) {

      Object.entries(error || {}).forEach(([field, messages]) => {
        (Array.isArray(messages) ? messages : [messages]).forEach((msg) =>
          Notifier.error(`${field}: ${msg}`)
        );
      });
    } finally {
      setLoading(false);
    }
  };




  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content recevingPaymentBody" style={{
        position: 'relative',
        width: '50%',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        height: '94vh'
      }}>
        <div
          className="modal-header-content"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "10px",
          }}
        >
          <h2 className="text-primary">
            {mode === "edit" ? "Edit Bank Account" : "Add Bank Account"}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <img src={close} alt="close" />
          </button>
        </div>

        {/* Type of Account */}
        <div className="row mt-2">
          <div className="col-md-12">
            <label className="form-labell">
              Type of Account<span className="astrisk">*</span>
            </label>
            <Select
              options={accountTypeOptions}
              value={
                accountTypeOptions.find((opt) => opt.value === formData.accountType) || null
              }
              onChange={handleAccountTypeChange}
              placeholder="Select Account Type"
              styles={reactSelectStyles}
            />
            {errors.accountType && <small className="text-danger">{errors.accountType}</small>}
          </div>
        </div>

        {/* Bank Select */}
        <div className="row mt-2">
          <div className="col-md-12">
            <label className="form-labell">
              Select Bank name<span className="astrisk">*</span>
            </label>
            <Select
              options={bankOptions}
              value={bankOptions.find((b) => b.value === formData.bankId) || null}
              onChange={handleBankChange}
              placeholder="Select Bank"
              isSearchable
              styles={reactSelectStyles}
            />
            {errors.bankId && <small className="text-danger">{errors.bankId}</small>}
          </div>
        </div>

        {/* Account Holder Name */}
        <div className="row mt-2">
          <div className="col-md-12">
            <label className="form-labell">
              Account Holder Name<span className="astrisk">*</span>
            </label>
            <input
              type="text"
              className="fm-modal form-control"
              placeholder="Enter Name"
              name="accountHolderName"
              value={formData.accountHolderName}
              onChange={handleChange}
            />
            {errors.accountHolderName && (
              <small className="text-danger">{errors.accountHolderName}</small>
            )}
          </div>
        </div>

        {/* Account Number */}
        <div className="row mt-2">
          <div className="col-md-12">
            <label className="form-labell">
              Account Number<span className="astrisk">*</span>
            </label>
            <input
              type="text"
              className="fm-modal form-control"
              placeholder="Enter Account Number"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
            />
            {errors.accountNumber && (
              <small className="text-danger">{errors.accountNumber}</small>
            )}
          </div>
        </div>

        {/* IFSC Code */}
        <div className="row mt-2">
          <div className="col-md-12">
            <label className="form-labell">
              IFSC Code<span className="astrisk">*</span>
            </label>
            <input
              type="text"
              className="fm-modal form-control"
              placeholder="Enter IFSC Code"
              name="ifscCode"
              value={formData.ifscCode}
              onChange={handleChange}
            />
            {errors.ifscCode && <small className="text-danger">{errors.ifscCode}</small>}
          </div>
        </div>

        {/* MICR Code */}
        <div className="row mt-2">
          <div className="col-md-12">
            <label className="form-labell">
              MICR Code 
            </label>
          
            <input
              type="text"
              className="fm-modal form-control"
              placeholder="Enter MICR Code"
              name="micrCode"
              value={formData.micrCode}
              onChange={handleChange}
            />
            {errors.micrCode && <small className="text-danger">{errors.micrCode}</small>}
          </div>
        </div>

        {/* Branch Address */}
        <div className="row mt-2">
          <div className="col-md-12">
            <label className="form-labell">
              Branch Address<span className="astrisk">*</span>
            </label>
            <input
              type="text"
              className="fm-modal form-control"
              placeholder="Enter Branch Address"
              name="branchAddress"
              value={formData.branchAddress}
              onChange={handleChange}
            />
            {errors.branchAddress && (
              <small className="text-danger">{errors.branchAddress}</small>
            )}
          </div>
        </div>

        {/* Default Account Checkbox */}
        <div className="row mt-3">
          <div className="col-md-6">
            <label className="form-labell">
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={handleDefaultChange}
                className="me-2"
              />
              Set as Default Account
            </label>
          </div>
          <div className="col-md-6">
            <label className="form-labell">
              <input
                type="checkbox"
                checked={formData.isPGDefault}
                onChange={handlePGDefaultChange}
                className="me-2"
              />
              Set as Default For PG Account
            </label>
          </div>
        </div>

        {/* Submit */}
        <div className="row mt-4">
          <div className="col-md-12 d-flex justify-content-end">
            <button type="submit" className="add-btn" onClick={handleSubmit}>
              {loading ? (
                <SpinnerLoader color="#FFFFFF" />
              ) : mode === "edit" ? (
                "Update Account Details"
              ) : (
                "Create Account"
              )}
              <img className="ms-2" src={next} alt="next" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
